import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper,
  CircularProgress,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InventoryIcon from '@mui/icons-material/Inventory';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUp from '@mui/icons-material/TrendingUp';
import Warning from '@mui/icons-material/Warning';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PODetailsDialog from './PODetailsDialog';
import BusinessInsightsDialog from './BusinessInsightsDialog';
import { inventoryService } from '../services/inventory.service';

const WarehouseInventory = () => {
  const { warehouseId } = useParams();
  const navigate = useNavigate();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warehouseInfo, setWarehouseInfo] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sorting state
  const [orderBy, setOrderBy] = useState('expiration_date');
  const [order, setOrder] = useState('asc');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Column visibility toggle
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // PO Dialog state
  const [selectedPO, setSelectedPO] = useState(null);
  const [poDialogOpen, setPODialogOpen] = useState(false);

  // Business Insights Dialog state
  const [selectedSkuData, setSelectedSkuData] = useState(null);
  const [insightsDialogOpen, setInsightsDialogOpen] = useState(false);

  // Expanded rows state
  const [expandedSkus, setExpandedSkus] = useState(new Set());

  // Column configuration for grouped SKU view
  const [columns, setColumns] = useState([
    { id: 'expand', label: '', visible: true, sortable: false, width: 50 },
    { id: 'sku', label: 'SKU', visible: true, sortable: true, width: 120 },
    { id: 'business_insights', label: '', visible: warehouseId === 'WH-20882088', sortable: false, width: 80 },
    { id: 'product_name', label: 'Product Name', visible: true, sortable: true, width: 200 },
    { id: 'total_quantity', label: 'Total Quantity', visible: true, sortable: true, width: 130, numeric: true },
    { id: 'unique_lots', label: 'Lots', visible: true, sortable: true, width: 80, numeric: true },
    { id: 'lots', label: 'Lot Numbers', visible: false, sortable: false, width: 150 },
    { id: 'unique_areas', label: 'Areas', visible: true, sortable: true, width: 80, numeric: true },
    { id: 'areas', label: 'Areas', visible: false, sortable: false, width: 120 },
    { id: 'unique_zones', label: 'Zones', visible: true, sortable: true, width: 80, numeric: true },
    { id: 'zones', label: 'Zones', visible: false, sortable: false, width: 120 },
    { id: 'unique_bin_locations', label: 'Bins', visible: true, sortable: true, width: 80, numeric: true },
    { id: 'bin_locations', label: 'Bin Locations', visible: false, sortable: false, width: 150 },
    { id: 'min_expiration_date', label: 'Earliest Expiration', visible: true, sortable: true, width: 140 },
    { id: 'max_expiration_date', label: 'Latest Expiration', visible: true, sortable: true, width: 140 },
    { id: 'expired_count', label: 'Expired Lots', visible: true, sortable: true, width: 100, numeric: true },
    { id: 'expiring_soon_count', label: 'Expiring Soon', visible: true, sortable: true, width: 120, numeric: true },
    { id: 'unique_po_numbers', label: 'POs', visible: true, sortable: true, width: 80, numeric: true },
    { id: 'po_numbers', label: 'PO Numbers', visible: false, sortable: false, width: 150 },
    { id: 'unique_shipment_ids', label: 'Shipments', visible: true, sortable: true, width: 100, numeric: true },
    { id: 'shipment_ids', label: 'Shipment IDs', visible: false, sortable: false, width: 150 }
  ]);

  const handleColumnToggleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnToggleClose = () => {
    setAnchorEl(null);
  };

  const toggleColumnVisibility = (colId) => {
    setColumns(prev => prev.map(col => 
      col.id === colId ? { ...col, visible: !col.visible } : col
    ));
  };

  const toggleExpandedSku = (sku) => {
    setExpandedSkus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
      }
      return newSet;
    });
  };

  // Get lot details for a specific SKU
  const getLotDetails = (sku) => {
    return rowData.filter(item => item.sku === sku);
  };

  // Business insights logic for WH-20882088
  const getBusinessInsights = (item) => {
    if (warehouseId !== 'WH-20882088') return null;
    
    const insights = [];
    
    // Hardcoded High Risk Alert - only for SKU 1692502122
    if (item.sku === '1692502122') {
      insights.push({
        type: 'high_risk_alert',
        icon: <Warning sx={{ color: 'error.main', fontSize: 16 }} />,
        tooltip: 'High Risk Alert: Order pattern exceeded - 22 days since last order'
      });
    }
    
    // Check for high quantity (above 2000)
    if (item.total_quantity > 2000) {
      insights.push({
        type: 'high_quantity',
        icon: <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />,
        tooltip: 'High inventory level - consider reducing stock'
      });
    }
    
    // Check for expiring soon lots
    if (item.expiring_soon_count > 0) {
      insights.push({
        type: 'expiring_soon',
        icon: <Warning sx={{ color: 'warning.main', fontSize: 16 }} />,
        tooltip: `${item.expiring_soon_count} lot(s) expiring soon`
      });
    }
    
    // Check for expired lots
    if (item.expired_count > 0) {
      insights.push({
        type: 'expired',
        icon: <Warning sx={{ color: 'error.main', fontSize: 16 }} />,
        tooltip: `${item.expired_count} expired lot(s) - immediate action required`
      });
    }
    
    // Check for multiple locations (spread across areas/zones)
    if (item.unique_areas > 2 || item.unique_zones > 2) {
      insights.push({
        type: 'scattered',
        icon: <InsightsIcon sx={{ color: 'info.main', fontSize: 16 }} />,
        tooltip: 'Inventory scattered across multiple locations'
      });
    }
    
    // Check for optimal stock level (around 2000)
    if (item.total_quantity >= 1800 && item.total_quantity <= 2200 && item.expired_count === 0 && item.expiring_soon_count === 0) {
      insights.push({
        type: 'optimal',
        icon: <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />,
        tooltip: 'Optimal inventory level and condition'
      });
    }
    
    return insights;
  };

  // Update column visibility when warehouse changes
  useEffect(() => {
    setColumns(prev => prev.map(col => 
      col.id === 'business_insights' 
        ? { ...col, visible: warehouseId === 'WH-20882088' }
        : col
    ));
  }, [warehouseId]);

  // Fetch warehouse-specific inventory data
  useEffect(() => {
    fetchWarehouseInventoryData();
  }, [warehouseId]);

  const fetchWarehouseInventoryData = async () => {
    try {
      console.log('Fetching warehouse inventory data for:', warehouseId);
      setLoading(true);
      setError(null);
      
      // Using inventoryService to get warehouse-specific inventory
      const result = await inventoryService.getCurrentInventoryByWarehouse(warehouseId);
      
      console.log('Received warehouse data:', result);
      
      if (result.status === 'success' && result.inventory_data && result.inventory_data.data) {
        // Extract inventory data
        const inventoryItems = result.inventory_data.data;
        console.log('Setting warehouse inventory items:', inventoryItems);
        setRowData(inventoryItems);
        
        // Calculate warehouse summary from the inventory data
        if (inventoryItems.length > 0) {
          const firstItem = inventoryItems[0];
          const totalQuantity = inventoryItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
          const uniqueSkus = new Set(inventoryItems.map(item => item.sku)).size;
          
          setWarehouseInfo({
            warehouse_id: firstItem.warehouse_id || warehouseId,
            warehouse_name: firstItem.warehouse_name || warehouseId,
            total_skus: uniqueSkus,
            total_quantity: totalQuantity,
            in_stock_skus: inventoryItems.filter(item => (item.quantity || 0) > 0).length,
            low_stock_skus: 0, // Would need business logic to determine
            out_of_stock_skus: inventoryItems.filter(item => (item.quantity || 0) === 0).length
          });
        }
        
        setLoading(false);
      } else {
        console.error('Invalid warehouse response structure:', result);
        setError('Invalid response structure from API');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching warehouse inventory data:', error);
      setError('Failed to fetch warehouse inventory data');
      setLoading(false);
    }
  };

  // Sorting functions
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  // Pagination functions
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Group and aggregate data by SKU
  const groupedAndSortedData = React.useMemo(() => {
    let filtered = rowData;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.lot_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Group by SKU and aggregate data
    const groupedData = filtered.reduce((acc, item) => {
      const sku = item.sku;
      if (!acc[sku]) {
        acc[sku] = {
          sku: sku,
          product_name: item.product_name,
          product_id: item.product_id,
          total_quantity: 0,
          lots: [],
          areas: new Set(),
          zones: new Set(),
          bin_locations: new Set(),
          expiration_dates: [],
          po_numbers: new Set(),
          shipment_ids: new Set(),
          min_expiration_date: null,
          max_expiration_date: null,
          expiring_soon_count: 0,
          expired_count: 0
        };
      }
      
      // Aggregate quantities
      acc[sku].total_quantity += (item.quantity || 0);
      
      // Collect unique values
      acc[sku].lots.push(item.lot_number);
      acc[sku].areas.add(item.area);
      acc[sku].zones.add(item.zone);
      acc[sku].bin_locations.add(item.bin_location);
      if (item.original_po_number) acc[sku].po_numbers.add(item.original_po_number);
      if (item.received_shipment_id) acc[sku].shipment_ids.add(item.received_shipment_id);
      
      // Track expiration dates
      if (item.expiration_date) {
        acc[sku].expiration_dates.push(item.expiration_date);
        const expDate = new Date(item.expiration_date);
        if (!acc[sku].min_expiration_date || expDate < acc[sku].min_expiration_date) {
          acc[sku].min_expiration_date = expDate;
        }
        if (!acc[sku].max_expiration_date || expDate > acc[sku].max_expiration_date) {
          acc[sku].max_expiration_date = expDate;
        }
        
        // Count expiration status
        if (item.expiration_status === 'expired') {
          acc[sku].expired_count++;
        } else if (item.expiration_status === 'expiring_soon') {
          acc[sku].expiring_soon_count++;
        }
      }
      
      return acc;
    }, {});
    
    // Convert grouped data to array and format
    const groupedArray = Object.values(groupedData).map(group => ({
      ...group,
      lots: [...new Set(group.lots)].join(', '),
      areas: [...group.areas].join(', '),
      zones: [...group.zones].join(', '),
      bin_locations: [...group.bin_locations].join(', '),
      po_numbers: [...group.po_numbers].join(', '),
      shipment_ids: [...group.shipment_ids].join(', '),
      unique_lots: group.lots.length,
      unique_areas: group.areas.size,
      unique_zones: group.zones.size,
      unique_bin_locations: group.bin_locations.size,
      unique_po_numbers: group.po_numbers.size,
      unique_shipment_ids: group.shipment_ids.size
    }));
    
    // Apply sorting
    return groupedArray.sort(getComparator(order, orderBy));
  }, [rowData, searchTerm, order, orderBy]);

  // Get paginated data
  const paginatedData = React.useMemo(() => {
    return groupedAndSortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [groupedAndSortedData, page, rowsPerPage]);

  // Custom cell renderers for grouped SKU view
  const renderExpirationCell = (item, columnId) => {
    const date = item[columnId];
    if (!date) return '-';
    
    return (
      <Typography variant="body2">
        {new Date(date).toLocaleDateString()}
      </Typography>
    );
  };

  const renderExpirationStatusCell = (item, columnId) => {
    const count = item[columnId];
    if (count === 0) return '-';
    
    let color = 'default';
    if (columnId === 'expired_count') {
      color = 'error';
    } else if (columnId === 'expiring_soon_count') {
      color = 'warning';
    }
    
    return (
      <Chip 
        label={count} 
        color={color} 
        size="small" 
        variant="outlined"
      />
    );
  };

  const renderPOCell = (item) => {
    const poNumbers = item.po_numbers;
    if (!poNumbers) return '-';
    
    // If there's only one PO number, make it clickable
    if (item.unique_po_numbers === 1) {
      return (
        <Typography
          variant="body2"
          sx={{ 
            color: 'primary.main', 
            cursor: 'pointer', 
            textDecoration: 'underline',
            '&:hover': { color: 'primary.dark' }
          }}
          onClick={() => {
            setSelectedPO(poNumbers);
            setPODialogOpen(true);
          }}
        >
          {poNumbers}
        </Typography>
      );
    }
    
    // If multiple PO numbers, show count and full list in tooltip
    return (
      <Typography variant="body2">
        {item.unique_po_numbers} PO{item.unique_po_numbers > 1 ? 's' : ''}
      </Typography>
    );
  };

  const renderQuantityCell = (item) => {
    return (
      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
        {item.total_quantity?.toLocaleString()}
      </Typography>
    );
  };

  const renderBusinessInsightsCell = (item) => {
    if (warehouseId !== 'WH-20882088') return null;
    
    const insights = getBusinessInsights(item);
    
    if (!insights || insights.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
        {insights.map((insight, index) => (
          <Box
            key={`${insight.type}-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
            title={insight.tooltip}
            onClick={() => {
              setSelectedSkuData(item);
              setInsightsDialogOpen(true);
            }}
          >
            {insight.icon}
          </Box>
        ))}
      </Box>
    );
  };

  const renderExpandCell = (item) => {
    const isExpanded = expandedSkus.has(item.sku);
    const hasLots = item.unique_lots > 1;
    
    if (!hasLots) return null;
    
    return (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          toggleExpandedSku(item.sku);
        }}
        sx={{ p: 0 }}
      >
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    );
  };

  const renderCell = (item, column) => {
    switch (column.id) {
      case 'expand':
        return renderExpandCell(item);
      case 'min_expiration_date':
      case 'max_expiration_date':
        return renderExpirationCell(item, column.id);
      case 'expired_count':
      case 'expiring_soon_count':
        return renderExpirationStatusCell(item, column.id);
      case 'po_numbers':
        return renderPOCell(item);
      case 'total_quantity':
        return renderQuantityCell(item);
      case 'business_insights':
        return renderBusinessInsightsCell(item);
      default:
        return item[column.id] || '-';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/inventory');
          }}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <InventoryIcon sx={{ mr: 0.5 }} />
          Inventory
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <WarehouseIcon sx={{ mr: 0.5 }} />
          {warehouseInfo?.warehouse_name || warehouseId}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Warehouse: {warehouseInfo?.warehouse_name || warehouseId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ID: {warehouseInfo?.warehouse_id || warehouseId}
          </Typography>
        </Box>
        <IconButton onClick={handleColumnToggleClick}>
          <ViewColumnIcon />
        </IconButton>
      </Box>

      {/* Warehouse Summary Cards */}
      {warehouseInfo && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total SKUs
                </Typography>
                <Typography variant="h4">
                  {warehouseInfo.total_skus?.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Quantity
                </Typography>
                <Typography variant="h4">
                  {warehouseInfo.total_quantity?.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  In Stock SKUs
                </Typography>
                <Typography variant="h4" color="success.main">
                  {warehouseInfo.in_stock_skus?.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Low/Out of Stock
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {(warehouseInfo.low_stock_skus + warehouseInfo.out_of_stock_skus)?.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by SKU or Product Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleColumnToggleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List sx={{ width: 200 }}>
          {columns.map((col) => (
            <ListItem key={col.id} dense>
              <ListItemIcon>
                <Checkbox
                  checked={col.visible}
                  onChange={() => toggleColumnVisibility(col.id)}
                />
              </ListItemIcon>
              <ListItemText primary={col.label} />
            </ListItem>
          ))}
        </List>
      </Popover>
      
      {rowData.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Typography variant="h6" color="textSecondary">No inventory data available for this warehouse</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.50',
                '& .MuiTableCell-head': {
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 1.5
                }
              }}>
                {columns.filter(col => col.visible).map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === column.id ? order : false}
                    sx={{ width: column.width }}
                  >
                    {column.id === 'business_insights' ? (
                      <Tooltip title="Business Insights - Hover over icons for details" placement="top">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <InsightsIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        </Box>
                      </Tooltip>
                    ) : column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleRequestSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((item) => (
                <React.Fragment key={item.sku}>
                  <TableRow 
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      '& .MuiTableCell-body': {
                        py: 1.25
                      }
                    }}
                  >
                    {columns.filter(col => col.visible).map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.numeric ? 'right' : 'left'}
                      >
                        {renderCell(item, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                  
                  {/* Expanded lot details */}
                  {expandedSkus.has(item.sku) && (
                    <TableRow>
                      <TableCell colSpan={columns.filter(col => col.visible).length} sx={{ p: 0 }}>
                        <Box sx={{ bgcolor: 'grey.50', p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Lot Details for SKU: {item.sku}
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Lot Number</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Area</TableCell>
                                <TableCell>Zone</TableCell>
                                <TableCell>Bin Location</TableCell>
                                <TableCell>Expiration Date</TableCell>
                                <TableCell>PO Number</TableCell>
                                <TableCell>Shipment ID</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {getLotDetails(item.sku).map((lot) => (
                                <TableRow key={`${lot.inventory_id}-${lot.lot_number}`}>
                                  <TableCell>{lot.lot_number}</TableCell>
                                  <TableCell>{lot.quantity?.toLocaleString()}</TableCell>
                                  <TableCell>{lot.area}</TableCell>
                                  <TableCell>{lot.zone}</TableCell>
                                  <TableCell>{lot.bin_location}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={lot.expiration_date} 
                                      color={lot.expiration_status === 'expired' ? 'error' : 
                                             lot.expiration_status === 'expiring_soon' ? 'warning' : 'default'} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {lot.original_po_number ? (
                                      <Typography
                                        variant="body2"
                                        sx={{ 
                                          color: 'primary.main', 
                                          cursor: 'pointer', 
                                          textDecoration: 'underline',
                                          '&:hover': { color: 'primary.dark' }
                                        }}
                                        onClick={() => {
                                          setSelectedPO(lot.original_po_number);
                                          setPODialogOpen(true);
                                        }}
                                      >
                                        {lot.original_po_number}
                                      </Typography>
                                    ) : '-'}
                                  </TableCell>
                                  <TableCell>{lot.received_shipment_id || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={groupedAndSortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
      
      {/* PO Details Dialog */}
      <PODetailsDialog
        open={poDialogOpen}
        onClose={() => setPODialogOpen(false)}
        poNumber={selectedPO}
      />

      {/* Business Insights Dialog */}
      <BusinessInsightsDialog
        open={insightsDialogOpen}
        onClose={() => setInsightsDialogOpen(false)}
        skuData={selectedSkuData}
        warehouseId={warehouseId}
      />
      
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="body2">
            Debug: Loading: {loading.toString()}, Raw items: {rowData.length}, Grouped SKUs: {groupedAndSortedData.length}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WarehouseInventory; 